/**
 * Swatch Image Overrides
 *
 * Maps a Shopify product handle + color name to a custom local fabric image
 * used in the small color swatches on PLP cards and PDP color selectors
 * (instead of the Shopify variant image).
 *
 * Lookup is case-insensitive on color name and tolerates trailing spaces.
 */

import blackoutSwatchGreen from '@/assets/images/products/blackout/swatch-blackout-green.jpg';
import blackoutSwatchBlue from '@/assets/images/products/blackout/swatch-blackout-blue.jpg';
import blackoutSwatchGrey from '@/assets/images/products/blackout/swatch-blackout-grey.jpg';
import blackoutSwatchYellow from '@/assets/images/products/blackout/swatch-blackout-yellow.jpg';
import blackoutSwatchPurple from '@/assets/images/products/blackout/swatch-blackout-purple.jpg';
import blackoutSwatchPink from '@/assets/images/products/blackout/swatch-blackout-pink.jpg';
import blackoutSwatchTerracotta from '@/assets/images/products/blackout/swatch-blackout-terracotta.jpg';
import blackoutSwatchBeige from '@/assets/images/products/blackout/swatch-blackout-beige.jpg';

import linenSwatchBeige from '@/assets/images/products/linen/swatch-linen-beige.jpg';
import linenSwatchBlue from '@/assets/images/products/linen/swatch-linen-blue.jpg';
import linenSwatchGreen from '@/assets/images/products/linen/swatch-linen-green.jpg';
import linenSwatchYellow from '@/assets/images/products/linen/swatch-linen-yellow.jpg';
import linenSwatchPurple from '@/assets/images/products/linen/swatch-linen-purple.jpg';
import linenSwatchWhite from '@/assets/images/products/linen/swatch-linen-white.jpg';
import linenSwatchTerracotta from '@/assets/images/products/linen/swatch-linen-terracotta.jpg';
import linenSwatchPink from '@/assets/images/products/linen/swatch-linen-pink.jpg';
import linenSwatchBrown from '@/assets/images/products/linen/swatch-linen-brown.jpg';

import thickLinenSwatchBeige from '@/assets/images/products/linen-thick/swatch-thick-linen-beige.jpg';
import thickLinenSwatchBlue from '@/assets/images/products/linen-thick/swatch-thick-linen-blue.jpg';
import thickLinenSwatchGreige from '@/assets/images/products/linen-thick/swatch-thick-linen-greige.jpg';
import thickLinenSwatchGreen from '@/assets/images/products/linen-thick/swatch-thick-linen-green.jpg';
import thickLinenSwatchYellow from '@/assets/images/products/linen-thick/swatch-thick-linen-yellow.jpg';
import thickLinenSwatchPurple from '@/assets/images/products/linen-thick/swatch-thick-linen-purple.jpg';
import thickLinenSwatchWhite from '@/assets/images/products/linen-thick/swatch-thick-linen-white.jpg';
import thickLinenSwatchPink from '@/assets/images/products/linen-thick/swatch-thick-linen-pink.jpg';
import thickLinenSwatchTerracotta from '@/assets/images/products/linen-thick/swatch-thick-linen-terracotta.jpg';

// HD versions for large hero/carousel display
import thickLinenSwatchBeigeHD from '@/assets/images/products/linen-thick/swatch-thick-linen-beige-hd.jpg';
import thickLinenSwatchBlueHD from '@/assets/images/products/linen-thick/swatch-thick-linen-blue-hd.jpg';
import thickLinenSwatchGreigeHD from '@/assets/images/products/linen-thick/swatch-thick-linen-greige-hd.jpg';
import thickLinenSwatchGreenHD from '@/assets/images/products/linen-thick/swatch-thick-linen-green-hd.jpg';
import thickLinenSwatchYellowHD from '@/assets/images/products/linen-thick/swatch-thick-linen-yellow-hd.jpg';
import thickLinenSwatchPurpleHD from '@/assets/images/products/linen-thick/swatch-thick-linen-purple-hd.jpg';
import thickLinenSwatchPinkHD from '@/assets/images/products/linen-thick/swatch-thick-linen-pink-hd.jpg';
import thickLinenSwatchTerracottaHD from '@/assets/images/products/linen-thick/swatch-thick-linen-terracotta-hd.jpg';
import thickLinenSwatchWhiteHD from '@/assets/images/products/linen-thick/swatch-thick-linen-white-hd.jpg';

import blackoutSwatchBeigeHD from '@/assets/images/products/blackout/swatch-blackout-beige-hd.jpg';
import blackoutSwatchBlueHD from '@/assets/images/products/blackout/swatch-blackout-blue-hd.jpg';
import blackoutSwatchGreenHD from '@/assets/images/products/blackout/swatch-blackout-green-hd.jpg';
import blackoutSwatchGreyHD from '@/assets/images/products/blackout/swatch-blackout-grey-hd.jpg';
import blackoutSwatchYellowHD from '@/assets/images/products/blackout/swatch-blackout-yellow-hd.jpg';
import blackoutSwatchPurpleHD from '@/assets/images/products/blackout/swatch-blackout-purple-hd.jpg';
import blackoutSwatchPinkHD from '@/assets/images/products/blackout/swatch-blackout-pink-hd.jpg';
import blackoutSwatchTerracottaHD from '@/assets/images/products/blackout/swatch-blackout-terracotta-hd.jpg';

import linenSwatchBeigeHD from '@/assets/images/products/linen/swatch-linen-beige-hd.jpg';
import linenSwatchBlueHD from '@/assets/images/products/linen/swatch-linen-blue-hd.jpg';
import linenSwatchGreenHD from '@/assets/images/products/linen/swatch-linen-green-hd.jpg';
import linenSwatchYellowHD from '@/assets/images/products/linen/swatch-linen-yellow-hd.jpg';
import linenSwatchPurpleHD from '@/assets/images/products/linen/swatch-linen-purple-hd.jpg';
import linenSwatchTerracottaHD from '@/assets/images/products/linen/swatch-linen-terracotta-hd.jpg';
import linenSwatchWhiteHD from '@/assets/images/products/linen/swatch-linen-white-hd.jpg';
import linenSwatchBrownHD from '@/assets/images/products/linen/swatch-linen-brown-hd.jpg';
import linenSwatchPinkHD from '@/assets/images/products/linen/swatch-linen-pink-hd.jpg';

type SwatchMap = Record<string, string>;

// Maps Shopify color names (lowercased, trimmed) to local fabric swatch images.
const NERO_BLACKOUT_SWATCHES: SwatchMap = {
  'grön': blackoutSwatchGreen,
  'blå': blackoutSwatchBlue,
  'grå': blackoutSwatchGrey,
  'gul': blackoutSwatchYellow,
  'lila': blackoutSwatchPurple,
  'rosa': blackoutSwatchPink,
  'terracotta': blackoutSwatchTerracotta,
  'brun': blackoutSwatchBeige,
  'beige': blackoutSwatchBeige,
};

const TUNN_LINNE_SWATCHES: SwatchMap = {
  'vit': linenSwatchWhite,
  'beige': linenSwatchBeige,
  'brun': linenSwatchBrown,
  'blå': linenSwatchBlue,
  'grön': linenSwatchGreen,
  'gul': linenSwatchYellow,
  'lila': linenSwatchPurple,
  'aubergine': linenSwatchPurple,
  'terracotta': linenSwatchTerracotta,
  'rosa': linenSwatchPink,
};

const GROV_LINNE_SWATCHES: SwatchMap = {
  'vit': thickLinenSwatchWhite,
  'beige': thickLinenSwatchBeige,
  'greige': thickLinenSwatchGreige,
  'blå': thickLinenSwatchBlue,
  'grön': thickLinenSwatchGreen,
  'gul': thickLinenSwatchYellow,
  'lila': thickLinenSwatchPurple,
  'aubergin': thickLinenSwatchPurple,
  'aubergine': thickLinenSwatchPurple,
  'rosa': thickLinenSwatchPink,
  'terracotta': thickLinenSwatchTerracotta,
};

const SWATCH_IMAGE_OVERRIDES: Record<string, SwatchMap> = {
  'nero-mattbestalld-morklaggningsgardin': NERO_BLACKOUT_SWATCHES,
  'mattbestalld-tunn-linnegardin': TUNN_LINNE_SWATCHES,
  'mattbestalld-hissgardin-tunn-linne': TUNN_LINNE_SWATCHES,
  'mattbestalld-grov-linnegardin': GROV_LINNE_SWATCHES,
  'mattbestalld-hissgardin-grov-linne': GROV_LINNE_SWATCHES,
};

// HD overrides – used for large hero/carousel display only.
// Falls back to the standard SWATCH_IMAGE_OVERRIDES if no HD version exists.
const GROV_LINNE_HD_SWATCHES: SwatchMap = {
  'beige': thickLinenSwatchBeigeHD,
  'blå': thickLinenSwatchBlueHD,
  'greige': thickLinenSwatchGreigeHD,
  'grön': thickLinenSwatchGreenHD,
  'gul': thickLinenSwatchYellowHD,
  'lila': thickLinenSwatchPurpleHD,
  'aubergin': thickLinenSwatchPurpleHD,
  'aubergine': thickLinenSwatchPurpleHD,
  'rosa': thickLinenSwatchPinkHD,
  'terracotta': thickLinenSwatchTerracottaHD,
  'vit': thickLinenSwatchWhiteHD,
};

const NERO_BLACKOUT_HD_SWATCHES: SwatchMap = {
  'beige': blackoutSwatchBeigeHD,
  'brun': blackoutSwatchBeigeHD,
  'blå': blackoutSwatchBlueHD,
  'grön': blackoutSwatchGreenHD,
  'grå': blackoutSwatchGreyHD,
  'gul': blackoutSwatchYellowHD,
  'lila': blackoutSwatchPurpleHD,
  'rosa': blackoutSwatchPinkHD,
  'terracotta': blackoutSwatchTerracottaHD,
};

const TUNN_LINNE_HD_SWATCHES: SwatchMap = {
  'beige': linenSwatchBeigeHD,
  'blå': linenSwatchBlueHD,
  'grön': linenSwatchGreenHD,
  'gul': linenSwatchYellowHD,
  'lila': linenSwatchPurpleHD,
  'aubergine': linenSwatchPurpleHD,
  'terracotta': linenSwatchTerracottaHD,
  'vit': linenSwatchWhiteHD,
  'brun': linenSwatchBrownHD,
  'rosa': linenSwatchPinkHD,
};

const SWATCH_IMAGE_HD_OVERRIDES: Record<string, SwatchMap> = {
  'mattbestalld-grov-linnegardin': GROV_LINNE_HD_SWATCHES,
  'mattbestalld-hissgardin-grov-linne': GROV_LINNE_HD_SWATCHES,
  'nero-mattbestalld-morklaggningsgardin': NERO_BLACKOUT_HD_SWATCHES,
  'mattbestalld-tunn-linnegardin': TUNN_LINNE_HD_SWATCHES,
  'mattbestalld-hissgardin-tunn-linne': TUNN_LINNE_HD_SWATCHES,
};

const normalize = (value: string | null | undefined): string =>
  (value || '').toLowerCase().trim();

/**
 * Returns a custom swatch image for the given product handle + color name,
 * or null when no override exists.
 */
export function getSwatchImageOverride(
  handle: string | null | undefined,
  colorName: string | null | undefined
): string | null {
  if (!handle || !colorName) return null;
  const map = SWATCH_IMAGE_OVERRIDES[normalize(handle)];
  if (!map) return null;
  return map[normalize(colorName)] ?? null;
}

/**
 * Returns a high-resolution swatch image for the given product handle + color name,
 * intended for the large carousel display. Falls back to the standard override.
 */
export function getSwatchImageOverrideHD(
  handle: string | null | undefined,
  colorName: string | null | undefined
): string | null {
  if (!handle || !colorName) return null;
  const hdMap = SWATCH_IMAGE_HD_OVERRIDES[normalize(handle)];
  const hd = hdMap?.[normalize(colorName)];
  if (hd) return hd;
  return getSwatchImageOverride(handle, colorName);
}
